import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { 
  ClipboardList, Plus, Edit2, Trash2, Loader2, X, Copy, ExternalLink,
  Upload, Image as ImageIcon, BarChart3, Eye, EyeOff, Users
} from 'lucide-react';

const FRONTEND_BASE = window.location.origin;

const LeadForms = () => {
  const [forms, setForms] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [posterUploading, setPosterUploading] = useState(null);
  const [statsForm, setStatsForm] = useState(null);
  const [stats, setStats] = useState(null);

  const [formData, setFormData] = useState({
    batch_name: '',
    slug: '',
    headline: '',
    sub_headline: '',
    cta_text: 'Get a Call Back',
    primary_color: '#0891b2',
    youtube_link: '',
    product_id: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [formsRes, productsRes] = await Promise.all([
        api.getLeadForms(),
        api.getProducts(),
      ]);
      setForms(formsRes.data);
      setProducts(productsRes.data);
    } catch (err) {
      toast.error('Failed to load lead forms');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingForm(null);
    setFormData({
      batch_name: '',
      slug: '',
      headline: '',
      sub_headline: '',
      cta_text: 'Get a Call Back',
      primary_color: '#0891b2',
      youtube_link: '',
      product_id: '',
    });
    setShowModal(true);
  };

  const openEdit = (form) => {
    setEditingForm(form);
    setFormData({
      batch_name: form.batch_name || '',
      slug: form.slug || '',
      headline: form.headline || '',
      sub_headline: form.sub_headline || '',
      cta_text: form.cta_text || 'Get a Call Back',
      primary_color: form.primary_color || '#0891b2',
      youtube_link: form.youtube_link || '',
      product_id: form.product_id || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.batch_name.trim()) {
      toast.error('Batch name is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...formData };
      // Auto-derive slug if blank
      if (!payload.slug.trim()) {
        payload.slug = payload.batch_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      }
      if (!payload.product_id) delete payload.product_id;
      if (editingForm) {
        await api.updateLeadForm(editingForm.id, payload);
        toast.success('Form updated');
      } else {
        await api.createLeadForm(payload);
        toast.success('Form created');
      }
      setShowModal(false);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (form) => {
    if (!window.confirm(`Delete "${form.batch_name}" form? Submissions will be kept in Leads.`)) return;
    try {
      await api.deleteLeadForm(form.id);
      toast.success('Form deleted');
      await fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const toggleActive = async (form) => {
    try {
      await api.updateLeadForm(form.id, { is_active: !form.is_active });
      toast.success(form.is_active ? 'Form deactivated' : 'Form activated');
      await fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handlePosterUpload = async (form, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Poster must be under 10MB');
      return;
    }
    setPosterUploading(form.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.uploadLeadFormPoster(form.id, fd);
      toast.success('Poster uploaded');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setPosterUploading(null);
    }
  };

  const copyLink = (slug) => {
    const url = `${FRONTEND_BASE}/f/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const openStats = async (form) => {
    setStatsForm(form);
    setStats(null);
    try {
      const res = await api.getLeadFormSubmissions(form.id);
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to load stats');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="lead-forms-page" className="max-w-[1400px] mx-auto space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold font-headings uppercase tracking-tight text-slate-900 mb-2">
            Lead Forms
          </h1>
          <p className="text-slate-600 font-body max-w-2xl">
            Build branded public lead capture forms for batches. Share the link in YouTube event 
            descriptions; submissions land in the unassigned Leads pool ready for callers.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="create-form-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
        >
          <Plus size={18} />
          New Form
        </button>
      </div>

      {forms.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
          <ClipboardList size={56} className="mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No forms yet</h3>
          <p className="text-slate-500 mb-6">Create your first lead capture form for a batch.</p>
          <button
            onClick={openCreate}
            className="px-5 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
          >
            Create your first form
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {forms.map((form) => (
            <div
              key={form.id}
              data-testid={`form-card-${form.slug}`}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col"
            >
              {/* Poster preview / colour banner */}
              <div
                className="h-32 relative flex items-center justify-center text-white font-semibold"
                style={{ background: form.poster_url ? `url(${form.poster_url}) center/cover` : form.primary_color }}
              >
                {!form.poster_url && (
                  <span className="text-lg px-4 text-center drop-shadow">{form.batch_name}</span>
                )}
                {!form.is_active && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-sm font-bold uppercase">
                    Inactive
                  </div>
                )}
              </div>

              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-semibold text-slate-900 text-sm leading-tight mb-1 line-clamp-2">
                  {form.batch_name}
                </h3>
                <p className="text-xs text-slate-500 font-mono mb-3">/{form.slug}</p>

                <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                  <div className="bg-slate-50 rounded p-2">
                    <div className="text-slate-500">Submissions</div>
                    <div className="text-lg font-bold text-slate-900">{form.submission_count || 0}</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <div className="text-slate-500">Status</div>
                    <div className={`text-sm font-semibold ${form.is_active ? 'text-green-600' : 'text-slate-400'}`}>
                      {form.is_active ? 'Active' : 'Paused'}
                    </div>
                  </div>
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-1.5">
                  <button
                    onClick={() => copyLink(form.slug)}
                    title="Copy link"
                    data-testid={`copy-link-${form.slug}`}
                    className="flex-1 min-w-[80px] flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-cyan-50 text-cyan-700 rounded hover:bg-cyan-100"
                  >
                    <Copy size={12} /> Copy Link
                  </button>
                  <a
                    href={`/f/${form.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open form"
                    className="px-2 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                  >
                    <ExternalLink size={12} />
                  </a>
                  <button
                    onClick={() => openStats(form)}
                    title="View submissions"
                    data-testid={`stats-${form.slug}`}
                    className="px-2 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                  >
                    <BarChart3 size={12} />
                  </button>
                  <label
                    title="Upload poster"
                    className="px-2 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 cursor-pointer"
                  >
                    {posterUploading === form.id ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => { handlePosterUpload(form, e.target.files[0]); e.target.value = ''; }}
                    />
                  </label>
                  <button
                    onClick={() => toggleActive(form)}
                    title={form.is_active ? 'Pause' : 'Activate'}
                    className="px-2 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                  >
                    {form.is_active ? <EyeOff size={12} /> : <Eye size={12} />}
                  </button>
                  <button
                    onClick={() => openEdit(form)}
                    title="Edit"
                    data-testid={`edit-${form.slug}`}
                    className="px-2 py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    onClick={() => handleDelete(form)}
                    title="Delete"
                    className="px-2 py-1.5 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-xl font-semibold">{editingForm ? 'Edit Form' : 'Create Lead Form'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Batch Name *</label>
                <input
                  type="text"
                  data-testid="input-batch-name"
                  value={formData.batch_name}
                  onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                  placeholder="e.g., Railway NTPC Foundation 2026"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  URL Slug
                  <span className="text-xs font-normal text-slate-500 ml-2">
                    Form will be at {FRONTEND_BASE}/f/<span className="font-mono">{formData.slug || '...'}</span>
                  </span>
                </label>
                <input
                  type="text"
                  data-testid="input-slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  placeholder="auto-generated from batch name if empty"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 font-mono text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Headline (shown big on the form)</label>
                <input
                  type="text"
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  placeholder="e.g., Crack Railway NTPC 2026"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sub-headline</label>
                <textarea
                  value={formData.sub_headline}
                  onChange={(e) => setFormData({ ...formData, sub_headline: e.target.value })}
                  placeholder="e.g., Live classes by India's top mentors. Limited seats."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Primary Color (match poster)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="h-10 w-16 rounded border border-slate-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 font-mono text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CTA Button Text</label>
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    placeholder="Get a Call Back"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">YouTube / Social Link (optional)</label>
                <input
                  type="url"
                  value={formData.youtube_link}
                  onChange={(e) => setFormData({ ...formData, youtube_link: e.target.value })}
                  placeholder="https://youtube.com/..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Link to Product (optional — pre-fills product on captured leads)
                </label>
                <select
                  value={formData.product_id}
                  onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="">— None —</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} (₹{p.price})</option>
                  ))}
                </select>
              </div>

              {!editingForm && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex gap-2">
                  <Upload size={14} className="flex-shrink-0 mt-0.5" />
                  <span>After creating the form, click the image icon on the form card to upload the batch poster.</span>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-form-btn"
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting && <Loader2 className="animate-spin" size={16} />}
                  {editingForm ? 'Save Changes' : 'Create Form'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats modal */}
      {statsForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-semibold">{statsForm.batch_name}</h2>
                <p className="text-xs text-slate-500 font-mono">/{statsForm.slug}</p>
              </div>
              <button onClick={() => { setStatsForm(null); setStats(null); }} className="p-1 text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              {!stats ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="bg-cyan-50 rounded-lg p-4">
                      <div className="text-xs text-cyan-700 mb-1">Submissions</div>
                      <div className="text-2xl font-bold text-cyan-900">{stats.total_submissions}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-xs text-green-700 mb-1">Converted</div>
                      <div className="text-2xl font-bold text-green-900">{stats.converted}</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="text-xs text-slate-600 mb-1">Conversion %</div>
                      <div className="text-2xl font-bold text-slate-900">
                        {stats.total_submissions > 0 ? ((stats.converted / stats.total_submissions) * 100).toFixed(1) : 0}%
                      </div>
                    </div>
                  </div>

                  {stats.submissions.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Users size={36} className="mx-auto mb-2 text-slate-300" />
                      No submissions yet
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                          <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Phone</th>
                            <th className="px-3 py-2 text-left">Assigned</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-left">When</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {stats.submissions.map((lead) => (
                            <tr key={lead.id} className="hover:bg-slate-50">
                              <td className="px-3 py-2 font-medium text-slate-900">{lead.name}</td>
                              <td className="px-3 py-2 font-mono text-xs">{lead.phone}</td>
                              <td className="px-3 py-2 text-slate-600">{lead.assigned_to_name || '—'}</td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  (lead.status === 'customer') ? 'bg-green-100 text-green-700' :
                                  (lead.status === 'new') ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {lead.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-500">
                                {new Date(lead.created_at).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadForms;
